import React, { useState, useEffect }   from "react";
import { useNavigate }        from "react-router-dom";
import {
    Box, Typography, TextField, Button, Alert, Collapse,
    InputAdornment, IconButton, Divider,
} from '@mui/material';
import VisibilityIcon    from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation }     from 'react-i18next';
import axios    from "../api/axiosConfig";
import { useAuth } from "../context/AuthContext";
import AuthLayout from "./AuthLayout";

/** Map raw server / Supabase error strings → short, friendly sentences. */
function getFriendlyLoginError(errorCode, t) {
    if (!errorCode) return t('auth.invalid_creds');
    const s = errorCode.toLowerCase();
    if (s.includes('email not found') || s.includes('not found'))
        return t('auth.email_not_found', 'No account found with that email address.');
    if (s.includes('too many') || s.includes('rate limit'))
        return t('auth.too_many_attempts', 'Too many attempts. Please wait a moment and try again.');
    // covers "Invalid login credentials", "invalid credentials", "wrong password", etc.
    return t('auth.invalid_creds');
}

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
        <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
        <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
        <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
    </svg>
);

export default function Login() {
    const { checkAuth } = useAuth();
    const { t }         = useTranslation();
    const [formData,     setFormData]     = useState({ email: "", password: "" });
    const [error,        setError]        = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [confirmed,    setConfirmed]    = useState(false);
    const navigate = useNavigate();

    // Detect Supabase's post-confirmation redirect: /login#access_token=...&type=signup
    useEffect(() => {
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        if (params.get('type') === 'signup') {
            setConfirmed(true);
            // Clean the hash from the URL without a page reload
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, []);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        try {
            await axios.post("/api/app_user/login", formData);
            await checkAuth();

            // Restore pending checkout if the user came from Checkout.js unauthenticated
            const pending = sessionStorage.getItem('pendingCheckout');
            if (pending) {
                try {
                    const { tourId, checkoutState, contact } = JSON.parse(pending);
                    sessionStorage.removeItem('pendingCheckout');
                    navigate(`/checkout/${tourId}`, {
                        state: { ...checkoutState, restoredContact: contact }
                    });
                    return;
                } catch { /* ignore parse errors, fall through to home */ }
            }

            navigate("/");
        } catch (err) {
            const errorCode = err.response?.data?.error;
            if (errorCode === 'EMAIL_NOT_CONFIRMED') {
                navigate("/confirm-email", { state: { email: formData.email } });
                return;
            }
            setError(getFriendlyLoginError(errorCode, t));
        }
    };

    return (
        <AuthLayout>
            <Collapse in={confirmed}>
                <Alert severity="success" sx={{ mb: 3, textAlign: "left" }}>
                    {t('auth.email_confirmed')}
                </Alert>
            </Collapse>
            <Typography variant="h3" align="center"
                        sx={{ color: 'var(--primary-color)', fontWeight: 800, mb: 4, fontFamily: "'Playfair Display', Georgia, serif" }}>
                {t('auth.login_title')}
            </Typography>
            <Button fullWidth variant="outlined" size="large"
                    startIcon={<GoogleIcon />}
                    onClick={async () => {
                        const array = new Uint8Array(32);
                        crypto.getRandomValues(array);
                        const verifier = btoa(String.fromCharCode(...array))
                            .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
                        const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
                        const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
                            .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
                        sessionStorage.setItem('pkce_verifier', verifier);
                        window.location.href =
                            `${process.env.REACT_APP_SUPABASE_URL}/auth/v1/authorize` +
                            `?provider=google` +
                            `&redirect_to=${encodeURIComponent(window.location.origin + '/oauth-callback')}` +
                            `&code_challenge=${challenge}` +
                            `&code_challenge_method=S256` +
                            `&prompt=select_account`;
                    }}
                    sx={{ mb: 2, py: 1.5, textTransform: 'none', borderColor: 'divider', color: 'text.primary' }}>
                {t('auth.continue_with_google', 'Continue with Google')}
            </Button>
            <Divider sx={{ mb: 2 }}>{t('auth.or', 'or')}</Divider>

            <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField margin="normal" required fullWidth
                           label={t('auth.email')} name="email"
                           autoComplete="email" autoFocus
                           value={formData.email} onChange={handleChange} />

                <TextField margin="normal" required fullWidth
                           name="password" label={t('auth.password')}
                           type={showPassword ? 'text' : 'password'}
                           autoComplete="current-password"
                           value={formData.password} onChange={handleChange}
                           InputProps={{
                               endAdornment: (
                                   <InputAdornment position="end">
                                       <IconButton
                                           onClick={() => setShowPassword(p => !p)}
                                           edge="end" size="small" tabIndex={-1}
                                           aria-label={showPassword ? 'Hide password' : 'Show password'}>
                                           {showPassword
                                               ? <VisibilityOffIcon fontSize="small" />
                                               : <VisibilityIcon    fontSize="small" />}
                                       </IconButton>
                                   </InputAdornment>
                               ),
                           }} />

                <Box sx={{ textAlign: "right", mt: 0.5 }}>
                    <Button size="small" component="a" href="/forgot-password"
                            sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                        {t('forgot password?')}
                    </Button>
                </Box>

                <Button type="submit" fullWidth variant="contained" size="large" sx={{ mt: 4, py: 2 }}>
                    {t('auth.login_btn')}
                </Button>

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </Box>

            <Typography variant="body2" align="center" sx={{ mt: 3, color: 'text.secondary' }}>
                {t('auth.no_account', "Don't have an account?")}{' '}
                <Box component="a"
                     href="/register"
                     sx={{ color: 'var(--primary-color)', fontWeight: 700, cursor: 'pointer', textDecoration: 'none',
                         '&:hover': { textDecoration: 'underline' } }}>
                    {t('auth.create_one', 'Create one')}
                </Box>
            </Typography>
        </AuthLayout>
    );
}
